import McmDroneConfig, { McmDroneType } from '~/tenants/mcm/performable/drone/McmDroneConfig';
import McmJob from '~/tenants/mcm/model/McmJob';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { TenantJobLine } from '~/lib/model';
import { Big } from 'big.js';
import { DeliverableType } from '~common/model/Deliverable';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { McmOrderContext } from '~/tenants/mcm/model/McmOrder';
import { match } from 'ts-pattern';

export default class McmDroneJob extends McmJob<typeof McmDroneConfig> {
  get performable() {
    return McmDroneConfig;
  }

  get deliverable() {
    return DeliverableType.AERIAL;
  }

  get media() {
    return PersonaType.PROVIDER;
  }

  defaultValue(): ZodVersionedMetadata<(typeof McmDroneConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      twilight: false,
      type: McmDroneType.PHOTO,
    };
  }

  static name(type: McmDroneType): string {
    let name = 'Motion Drone';

    switch (type) {
      case McmDroneType.PHOTO:
        name += ' Photos';
        break;
      case McmDroneType.VIDEO_FULL:
        name += ' Video';
        break;
      case McmDroneType.VIDEO_LIMITED:
        name += ' Video (Limited Coverage)';
        break;
      case McmDroneType.VIDEO_PHOTO:
        name += ' Video + Photos';
        break;
      default:
        name += ' Service';
        break;
    }

    return name;
  }

  get code() {
    let code = 'D';

    switch (this.metadata.type) {
      case McmDroneType.PHOTO:
        code += 'S';
        break;
      case McmDroneType.VIDEO_FULL:
        code += 'V';
        break;
      case McmDroneType.VIDEO_LIMITED:
        return 'DV-LIMIT';
      case McmDroneType.VIDEO_PHOTO:
        code += 'VS';
        break;
    }

    if (this.metadata.twilight) {
      code += 'TWI';
    }

    if (this.seconds > 0) {
      code += `${this.seconds}`;
    } else if (this.minimumPhotos > 0) {
      code += `${this.minimumPhotos}`;
    }

    return code;
  }

  revenueLines(): TenantJobLine[] {
    const lines: TenantJobLine[] = [];
    //
    // if (this.orderHasPerformable('photo') && this.buyer('crossroads')) {
    //   return lines;
    // }\

    const cost = McmDroneJob.cost(this.order, this.metadata.type);

    if (!this.metadata.twilight) {
      if (this.minimumPhotos > 0) {
        lines.push({
          id: 'daytime_photos',
          description: `Daytime Photos (up to ${this.minimumPhotos})`,
          compare: 'Daytime Photos',
          amount: cost.photo,
        });
      }

      if (this.seconds > 0) {
        lines.push({
          id: 'daytime_video',
          description: `Daytime Video (up to ${this.seconds} seconds)`,
          amount: cost.video,
          compare: 'Daytime Video',
          taxable: true,
        });
      }
    } else {
      if (this.minimumPhotos > 0) {
        lines.push({
          id: 'twilight_photos',
          description: `Twilight Photos (up to ${this.minimumPhotos})`,
          compare: 'Twilight Photos',
          amount: cost.photo.plus(cost.twilight),
        });
      }

      if (this.seconds > 0) {
        lines.push({
          id: 'twilight_video',
          description: `Twilight Video (up to ${this.seconds} seconds)`,
          amount: cost.video,
          taxable: true,
        });
      }
    }

    return lines;
  }

  static cost(context: McmOrderContext, type: McmDroneType) {
    const photo =
      context.buyer.metadata.crossroads || context.parent?.crossroads
        ? new Big('135')
        : type === McmDroneType.PHOTO || type === McmDroneType.VIDEO_PHOTO
        ? new Big('174.95')
        : new Big(0);

    const video = match(type)
      .with(McmDroneType.VIDEO_LIMITED, () => new Big('125'))
      .with(McmDroneType.VIDEO_FULL, () => new Big('199.95'))
      .with(McmDroneType.VIDEO_PHOTO, () => new Big('125'))
      .otherwise(() => new Big(0));
    const twilight = match(type)
      .with(McmDroneType.VIDEO_PHOTO, () => new Big('50'))
      .with(McmDroneType.VIDEO_FULL, () => new Big('50'))
      .with(McmDroneType.PHOTO, () => new Big('75'))
      .otherwise(() => new Big(0));

    return {
      photo,
      video,
      twilight,
      both: photo.plus(video),
    };
  }

  static twilightCost(type: McmDroneType) {
    if (type == McmDroneType.VIDEO_PHOTO) {
      return new Big('50');
    }

    return new Big('75');
  }

  get seconds(): number {
    if (this.metadata.type !== McmDroneType.PHOTO) {
      return 90;
    }

    return 0;
  }

  get minimumPhotos(): number {
    if (this.metadata.type === McmDroneType.PHOTO || this.metadata.type === McmDroneType.VIDEO_PHOTO) {
      return 16;
    }

    return 0;
  }

  onsite(): number {
    return 30;
  }

  get configurable() {
    return !this.buyer('crossroads');
  }

  get name() {
    return McmDroneJob.name(this.metadata.type);
  }

  get hidden() {
    return (
      Boolean(this.buyer('crossroads')) && (!this.orderHasPerformable('zillow') || this.isCrossroadsPackage('bronze'))
    );
  }
}
